'use client';

import { LoadingButton } from '@mui/lab';
import { Box, Button, Card, CardContent, Grid2, Stack, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { useRequest } from '../api/Request';
import {
    CameraAlt,
    Groups,
    Headset,
    QuestionAnswer,
    SatelliteAlt,
    TrendingUp,
    Videocam,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { User } from '../database/user';
import { useApi } from '../api/Api';
import { useEffect, useState } from 'react';
import LoadingPage from '../loading/LoadingPage';

interface SubscriptionPriceMatrixProps {
    user?: User;
    redirectPath: string;
}

const SubscriptionPriceMatrix: React.FC<SubscriptionPriceMatrixProps> = ({
    user,
    redirectPath,
}) => {
    const api = useApi();
    const subscriptionCheckoutRequest = useRequest();
    const artistRequest = useRequest();
    const navigate = useNavigate();
    const subscriptionCreationRequest = useRequest();
    const [artist, setArtist] = useState<User>();

    useEffect(() => {
        if (!artistRequest.isSent()) {
            api.getUserPublic('artistUsername')
                .then((resp) => {
                    setArtist(resp.data);
                    artistRequest.onSuccess(resp.data);
                })
                .catch((err) => {
                    console.error('Error fetching artist data:', err);
                    artistRequest.onFailure(err);
                });
        }
    }, [artistRequest, api]);

    const onSubscribe = () => {
        if (!user) {
            return;
        }

        api.subscriptionCheckout({
            successPath: `${redirectPath}`,
            cancelPath: `${redirectPath}`,
        })
            .then((resp) => {
                window.location.href = resp.data.url;
            })
            .catch((err: unknown) => {
                console.error('subscriptionCheckout: ', err);
                subscriptionCheckoutRequest.onFailure(err);
            });
    };

    if (artistRequest.isLoading()) {
        return <LoadingPage />;
    }

    if (!artist) {
        return null;
    }

    if (!artist.artistInfo?.subscriptionSettings?.activePriceId) {
        return (
            <Stack alignItems='center' p={10}>
                <Typography textAlign='center' variant='h6'>
                    {artist?.displayName} has not set up this feature yet, check back in
                    later.
                </Typography>
            </Stack>
        );
    }

    return (
        <Box maxWidth={800} mx='auto' width='100%'>
            <Grid2
                size={{
                    xs: 12,
                    sm: 4,
                }}
            >
                <motion.div
                    initial={{ opacity: 0, x: -24 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    whileHover={{ y: -4 }}
                    transition={{ duration: 0.35, ease: 'easeOut' }}
                    style={{ height: '100%' }}
                >
                <Card variant='outlined' sx={{ height: 1 }}>
                    <CardContent sx={{ height: 1 }}>
                        <Stack alignItems='center' spacing={3} height={1}>
                            <Stack alignItems='center'>
                                <Typography
                                    variant='subtitle1'
                                    fontWeight='bold'
                                    textAlign='center'
                                >
                                    Free Tier
                                </Typography>

                                <Typography variant='h6'>$0</Typography>
                            </Stack>

                            <Stack spacing={1} flexGrow={1}>
                                <Stack direction='row' spacing={1}>
                                    <Groups />
                                    <Typography>
                                        Interact with the inner circle
                                    </Typography>
                                </Stack>
                                <Stack direction='row' spacing={1}>
                                    <QuestionAnswer />
                                    <Typography>
                                        Ask {artist.displayName} questions, make requests,
                                        and more
                                    </Typography>
                                </Stack>
                            </Stack>

                            <Button
                                variant='contained'
                                disabled={!!user}
                                onClick={() => navigate('/signup')}
                                sx={{
                                    ...(user && {
                                        opacity: 0.5,
                                        pointerEvents: 'none',
                                        cursor: 'not-allowed',
                                    }),
                                }}
                            >
                                {!!user ? 'Already a Member' : 'Sign Up for Free'}
                            </Button>
                            {!user && (
                                <Button
                                    variant='text'
                                    sx={{ textTransform: 'none' }}
                                    disabled={!!user}
                                    onClick={() => navigate('/signin')}
                                >
                                    Already have an account? Sign In
                                </Button>
                            )}
                        </Stack>
                    </CardContent>
                </Card>
                </motion.div>
            </Grid2>
            <Grid2
                size={{
                    xs: 12,
                    sm: 4,
                }}
            >
                <motion.div
                    initial={{ opacity: 0, x: 24 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    whileHover={{ y: -6, scale: 1.02 }}
                    transition={{ duration: 0.35, ease: 'easeOut', delay: 0.1 }}
                    style={{ height: '100%' }}
                >
                <Card
                    variant='outlined'
                    sx={{
                        height: 1,
                        borderColor: 'primary.main',
                        borderWidth: '2px',
                        boxShadow: '0 0 20px rgba(139, 0, 0, 0.25)',
                        background: 'linear-gradient(135deg, rgba(139,0,0,0.12) 0%, rgba(30,30,30,0.98) 100%)',
                    }}
                >
                    <CardContent sx={{ height: 1 }}>
                        <Stack alignItems='center' spacing={3} height={1}>
                            <Stack alignItems='center'>
                                <Typography
                                    variant='subtitle1'
                                    fontWeight='bold'
                                    textAlign='center'
                                >
                                    {artist.artistInfo.subscriptionSettings.productName ||
                                        `True ${artist.displayName} Fan`}
                                </Typography>

                                <Typography variant='h6'>
                                    $
                                    {
                                        artist.artistInfo?.subscriptionSettings
                                            ?.subscriptionPriceInfo[
                                            artist.artistInfo.subscriptionSettings
                                                .activePriceId
                                        ].price
                                    }{' '}
                                    / month
                                </Typography>
                            </Stack>

                            <Stack spacing={1} flexGrow={1}>
                                <Stack direction='row' spacing={1}>
                                    <TrendingUp />
                                    <Typography>
                                        All features from the Free Tier
                                    </Typography>
                                </Stack>
                                <Stack direction='row' spacing={1}>
                                    <Headset />
                                    <Typography>
                                        Access to what nobody else gets to hear
                                    </Typography>
                                </Stack>
                                <Stack direction='row' spacing={1}>
                                    <Videocam />
                                    <Typography>Behind the scenes videos</Typography>
                                </Stack>
                                <Stack direction='row' spacing={1}>
                                    <CameraAlt />
                                    <Typography>
                                        Snapshots of {artist.displayName}'s journey
                                    </Typography>
                                </Stack>
                                <Stack direction='row' spacing={1}>
                                    <SatelliteAlt />
                                    <Typography>
                                        Exclusive forum discussions in EnterStellar Spacebar
                                    </Typography>
                                </Stack>
                            </Stack>
                            <LoadingButton
                                variant='contained'
                                loading={subscriptionCreationRequest?.isLoading()}
                                onClick={onSubscribe}
                                disabled={!user}
                                sx={{
                                    ...(!user && {
                                        opacity: 0.5,
                                        pointerEvents: 'none',
                                        cursor: 'not-allowed',
                                    }),
                                }}
                            >
                                {!user
                                    ? 'Must Sign Up/In Before Subscribing'
                                    : 'Subscribe'}
                            </LoadingButton>
                        </Stack>
                    </CardContent>
                </Card>
                </motion.div>
            </Grid2>
        </Box>
    );
};

export default SubscriptionPriceMatrix;
